"use client";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
  useMotionValueEvent,
} from "framer-motion";
import React, { useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Menu,
  X,
  ChevronDown,
  Sun,
  Moon,
  Globe,
  type LucideIcon,
} from "lucide-react";
import Link from "@/components/LocalizedLink";
import { Button } from "@/components/ui/cta-button";
import { useVisiblePathname } from "@/components/useVisiblePathname";
import Image from "next/image";
import { useTheme } from "@/components/theme-provider";
import { triggerThemeTransition } from "@/lib/utils/view-transition";
import { getMessages } from "@/lib/messages";
import { getLocaleFromPathname } from "@/lib/locale-path";
import type { Locale } from "@/lib/i18n";
import { COMPANY } from "@/lib/company";
import { FacebookIcon, LinkedInIcon, XIcon } from "@/components/brand-icons";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
  healthcareMenu,
  servicesMenu,
  companyMenu,
  navText,
  type NavColumn,
} from "@/data/navigation";

// --- Define our scroll ranges ---
const NAVBAR_SCROLL_RANGE = [0, 200];

const noopSubscribe = () => () => {};

type MegaMenuKey = "healthcare" | "services" | "company";
const MEGA_MENUS: MegaMenuKey[] = ["healthcare", "services", "company"];

// --- Helper component for the underline ---
const ActiveLinkIndicator = () => (
  <motion.div
    className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600"
    layoutId="active-link-indicator" // Enables sliding animation
    initial={false}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.2 }}
  />
);

// Internal routes go through LocalizedLink; mailto: etc. stay plain anchors.
const MenuLink = ({
  href,
  ...props
}: { href: string; children: React.ReactNode } & Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "href" | "children"
>) =>
  href.startsWith("/") ? <Link href={href} {...props} /> : <a href={href} {...props} />;

// Small orange line icon before a group heading (DESIGN_GUIDE §3.1: icon glyphs
// may use orange-500 as a fill; text stays neutral).
const GroupIcon = ({ icon: Icon }: { icon: LucideIcon }) => (
  <Icon aria-hidden="true" size={14} strokeWidth={1.75} className="shrink-0 text-orange-700 dark:text-orange-500" />
);

// --- Mega menu: grouped link columns ---
// Each group is an eyebrow heading over a hairline rule, then a plain link
// list — editorial rows, not icon cards (DESIGN_GUIDE §3.3).
const MenuColumns = ({
  columns,
  locale,
  onNavigate,
  footer,
}: {
  columns: NavColumn[];
  locale: Locale;
  onNavigate: () => void;
  /** Full-width row under all columns. */
  footer?: React.ReactNode;
}) => (
  <div>
  <div className="grid grid-cols-3 gap-x-10">
    {columns.map((column, i) => (
      <div key={i} className="flex flex-col gap-y-8">
        {column.map((group) => (
          <div key={group.heading.en}>
            <p className="flex items-center gap-2 border-b border-border pb-3 text-xs font-medium uppercase tracking-[0.22em] text-foreground/60">
              <GroupIcon icon={group.icon} />
              {navText(group.heading, locale)}
            </p>
            <ul className="mt-2 flex flex-col">
              {group.links.map((link) => (
                <li key={link.href}>
                  <MenuLink
                    href={link.href}
                    onClick={onNavigate}
                    className="group -mx-2 flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-sm text-foreground/80 transition-colors hover:text-orange-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 dark:hover:text-orange-500"
                  >
                    {navText(link.label, locale)}
                    <ArrowRight
                      aria-hidden="true"
                      className="h-3.5 w-3.5 shrink-0 -translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100 motion-reduce:transition-none"
                    />
                  </MenuLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    ))}
  </div>
  {footer}
  </div>
);

// --- Socials (Company menu) ---
// LinkedIn/Facebook/X URLs come from site settings (same as the footer);
// Clutch/GoodFirms/DesignRush profiles from lib/company.ts.
export interface NavSocialUrls {
  linkedinUrl?: string;
  facebookUrl?: string;
  xUrl?: string;
}

const socialLinks = (urls: NavSocialUrls) =>
  [
    { label: "LinkedIn", href: urls.linkedinUrl, icon: <LinkedInIcon className="h-3.5 w-3.5" /> },
    { label: "Facebook", href: urls.facebookUrl, icon: <FacebookIcon className="h-3.5 w-3.5" /> },
    { label: "X", href: urls.xUrl, icon: <XIcon className="h-3.5 w-3.5" /> },
    {
      label: "Clutch",
      href: COMPANY.clutchUrl,
      icon: <Image src="/footer/clutch.png" alt="" width={14} height={14} className="h-3.5 w-3.5" />,
    },
    {
      label: "GoodFirms",
      href: COMPANY.goodfirmsUrl,
      icon: <Image src="/footer/goodfirms.png" alt="" width={14} height={14} className="h-3.5 w-3.5" />,
    },
    {
      label: "DesignRush",
      href: COMPANY.designrushUrl,
      icon: <Image src="/footer/designrush-mark.svg" alt="" width={14} height={14} unoptimized className="h-3.5 w-3.5" />,
    },
  ].filter((s): s is typeof s & { href: string } => Boolean(s.href));

// "menu": one horizontal row under the desktop columns (hairline above, label
// then logos); "mobile": a list inside the Company accordion.
const SocialGroup = ({
  urls,
  title,
  variant,
}: {
  urls: NavSocialUrls;
  title: string;
  variant: "menu" | "mobile";
}) => {
  const links = socialLinks(urls);
  if (links.length === 0) return null;
  const isMenu = variant === "menu";
  return (
    <div
      className={
        isMenu
          ? "mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-border pt-5"
          : "pl-4 pt-4"
      }
    >
      <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-foreground/60">
        <GroupIcon icon={Globe} />
        {title}
      </p>
      <ul className={isMenu ? "flex flex-wrap items-center gap-x-6 gap-y-2" : "mt-1 flex flex-col"}>
        {links.map((s) => (
          <li key={s.label}>
            <a
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Woltrio on ${s.label} (opens in new tab)`}
              className={
                isMenu
                  ? "group flex items-center gap-2 rounded-md py-1 text-sm text-foreground/80 transition-colors hover:text-orange-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 dark:hover:text-orange-500"
                  : "flex items-center gap-3 py-2.5 text-lg"
              }
            >
              <span
                aria-hidden="true"
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-black/10"
              >
                {s.icon}
              </span>
              <span className={isMenu ? undefined : "flex-1"}>{s.label}</span>
              {isMenu && (
                <ArrowUpRight
                  aria-hidden="true"
                  className="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 motion-reduce:transition-none"
                />
              )}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

// --- Mega menu: dark feature panel (right column) ---
const MenuFeature = ({
  eyebrow,
  title,
  children,
  actions,
}: {
  eyebrow: string;
  title: string;
  children: React.ReactNode;
  actions: React.ReactNode;
}) => (
  <div
    className="dark relative flex flex-col overflow-hidden rounded-2xl p-7"
    style={{
      background:
        "radial-gradient(130% 90% at 70% -10%, color-mix(in oklch, var(--scene-ember) 14%, var(--scene-ink)) 0%, var(--scene-ink) 60%)",
    }}
  >
    <div aria-hidden="true" className="texture-grain pointer-events-none absolute inset-0 opacity-40" />
    <div className="relative flex flex-1 flex-col">
      <p className="text-xs font-medium uppercase tracking-[0.22em] text-zinc-400">
        {eyebrow}
      </p>
      <p className="font-display mt-4 text-2xl font-semibold leading-[1.1] tracking-[-0.02em] text-white">
        {title}
      </p>
      <div className="mt-4 text-sm leading-relaxed text-zinc-300">{children}</div>
      <div className="mt-auto flex flex-col items-start gap-4 pt-8">{actions}</div>
    </div>
  </div>
);

const featureTextLinkClass =
  "group inline-flex items-center gap-2 rounded-sm text-xs font-medium uppercase tracking-[0.22em] text-zinc-300 transition-colors hover:text-orange-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500";

const MegaMenuPanel = ({
  menu,
  locale,
  onNavigate,
  socials,
}: {
  menu: MegaMenuKey;
  locale: Locale;
  onNavigate: () => void;
  socials: NavSocialUrls;
}) => {
  const t = getMessages(locale);

  const panels: Record<
    MegaMenuKey,
    {
      columns: NavColumn[];
      eyebrow: string;
      title: string;
      body: React.ReactNode;
      primary: { href: string; label: string };
      secondary: { href: string; label: string };
    }
  > = {
    healthcare: {
      columns: healthcareMenu,
      eyebrow: t.nav.healthcarePanelEyebrow,
      title: t.nav.healthcarePanelTitle,
      body: (
        <ul className="flex flex-col gap-3">
          {[t.nav.healthcarePanelIso, t.nav.healthcarePanelBaa].map((line) => (
            <li key={line} className="flex gap-2.5">
              <span aria-hidden="true" className="mt-[0.45rem] h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      ),
      primary: { href: "/health-tech", label: t.nav.exploreHealthcare },
      secondary: { href: "/case-studies", label: t.nav.seeCaseStudies },
    },
    services: {
      columns: servicesMenu,
      eyebrow: t.nav.servicesPanelEyebrow,
      title: t.nav.servicesPanelTitle,
      body: <p>{t.nav.servicesPanelBody}</p>,
      primary: { href: "/services", label: t.nav.exploreServices },
      secondary: { href: "/case-studies", label: t.nav.seeCaseStudies },
    },
    company: {
      columns: companyMenu,
      eyebrow: t.nav.companyPanelEyebrow,
      title: t.nav.companyPanelTitle,
      body: <p>{t.nav.companyPanelBody}</p>,
      primary: { href: "/inquiry", label: t.nav.inquiryNow },
      secondary: { href: "/contact", label: t.nav.contact },
    },
  };
  const panel = panels[menu];

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_17rem] gap-10 p-8 xl:grid-cols-[minmax(0,1fr)_19rem] xl:p-10">
      <MenuColumns
        columns={panel.columns}
        locale={locale}
        onNavigate={onNavigate}
        footer={
          menu === "company" ? (
            <SocialGroup urls={socials} title={t.nav.socialsTitle} variant="menu" />
          ) : undefined
        }
      />
      <MenuFeature
        eyebrow={panel.eyebrow}
        title={panel.title}
        actions={
          <>
            <Button href={panel.primary.href} size="sm" onClick={onNavigate}>
              {panel.primary.label}
            </Button>
            <Link href={panel.secondary.href} onClick={onNavigate} className={featureTextLinkClass}>
              {panel.secondary.label}
              <ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />
            </Link>
          </>
        }
      >
        {panel.body}
      </MenuFeature>
    </div>
  );
};

// --- Mobile: the same groups, flattened under one accordion ---
const MobileMenuGroups = ({
  columns,
  locale,
}: {
  columns: NavColumn[];
  locale: Locale;
}) => (
  <>
    {columns.flat().map((group) => (
      <div key={group.heading.en} className="pl-4 pt-4">
        <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-foreground/60">
          <GroupIcon icon={group.icon} />
          {navText(group.heading, locale)}
        </p>
        <div className="mt-1 flex flex-col">
          {group.links.map((link) => (
            <MenuLink key={link.href} href={link.href} className="py-2.5 text-lg">
              {navText(link.label, locale)}
            </MenuLink>
          ))}
        </div>
      </div>
    ))}
  </>
);

// --- Mobile Menu Accordion Link (Unchanged) ---
const MobileMenuLink = ({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Close main menu when a sub-link is clicked
  const handleChildClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Check if the clicked element is a Link
    if ((e.target as HTMLElement).closest("a")) {
      onClose();
    }
  };

  return (
    <div>
      <button
        type="button"
        className="flex w-full items-center justify-between py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span>{title}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown size={24} />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="flex flex-col text-foreground"
            onClick={handleChildClick} // Use event delegation
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- Mobile Menu Component (REFACTORED) ---
const MobileMenu = ({ onClose, socials }: { onClose: () => void; socials: NavSocialUrls }) => {
  const { theme, setTheme } = useTheme();
  const pathname = useVisiblePathname();
  const locale = getLocaleFromPathname(pathname);
  const t = getMessages(locale);
  const mounted = React.useSyncExternalStore(noopSubscribe, () => true, () => false);

  // Get the actual theme being displayed (resolve "system" to "dark" or "light")
  const getDisplayTheme = () => {
    if (!mounted) return "dark"; // Default to dark during SSR
    if (theme === "system") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return theme;
  };

  const displayTheme = getDisplayTheme();

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="fixed top-0 left-0 w-full h-dvh bg-background text-foreground z-40 pt-24 px-6 pb-8 overflow-y-auto"
    >
      <div className="flex flex-col h-full">
        <div className="flex flex-col gap-y-4 text-2xl font-medium">
          {/* --- Theme Switcher Mobile --- */}
          <div className="flex items-center justify-between py-2 border-b border-border">
            <span>Theme</span>
            <button
              onClick={(e) =>
                triggerThemeTransition(e, () =>
                  setTheme(displayTheme === "dark" ? "light" : "dark"),
                )
              }
              className="p-2 rounded-full bg-muted cursor-pointer"
            >
              {displayTheme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>

          {/* --- Language Mobile --- */}
          <div className="flex items-center justify-between py-2 border-b border-border">
            <span>{t.footer.languageLabel}</span>
            <LanguageSwitcher variant="compact" label={t.footer.languageLabel} />
          </div>

          {/* --- Healthcare --- */}
          <MobileMenuLink title={t.nav.healthcare} onClose={onClose}>
            <Link
              href="/health-tech"
              className="pl-4 py-3 text-xl font-semibold text-orange-700 dark:text-orange-500"
            >
              {t.nav.exploreHealthcare}
            </Link>
            <MobileMenuGroups columns={healthcareMenu} locale={locale} />
          </MobileMenuLink>

          {/* --- Services --- */}
          <MobileMenuLink title={t.nav.services} onClose={onClose}>
            <MobileMenuGroups columns={servicesMenu} locale={locale} />
            <Link
              href="/services"
              className="pl-4 pt-4 pb-3 text-base font-semibold text-orange-700 dark:text-orange-500"
            >
              {t.nav.viewAllServices}
            </Link>
          </MobileMenuLink>

          <Link href="/case-studies" className="py-3" onClick={onClose}>
            {t.nav.work}
          </Link>
          <Link href="/insights" className="py-3" onClick={onClose}>
            {t.nav.insights}
          </Link>

          {/* --- Company --- */}
          <MobileMenuLink title={t.nav.company} onClose={onClose}>
            <MobileMenuGroups columns={companyMenu} locale={locale} />
            <SocialGroup urls={socials} title={t.nav.socialsTitle} variant="mobile" />
          </MobileMenuLink>
        </div>

        {/* Spacer to push button to bottom */}
        <div className="flex-grow"></div>

        <Button
          href={"/inquiry"}
          variant="primary"
          className="mt-8 w-full justify-center"
          onClick={onClose}
        >
          {t.nav.inquiryNow}
        </Button>
      </div>
    </motion.div>
  );
};

// --- MAIN NAVBAR COMPONENT ---
export function Navbar({ socials = {} }: { socials?: NavSocialUrls } = {}) {
  const pathname = useVisiblePathname();
  const locale = getLocaleFromPathname(pathname);
  const t = getMessages(locale);

  const { theme, setTheme } = useTheme();
  const { scrollY } = useScroll();
  const [hoveredMenu, setHoveredMenu] = useState<
    MegaMenuKey | "work" | "insights" | false
  >(false);
  const [lastMegaMenu, setLastMegaMenu] = useState<MegaMenuKey>("healthcare");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // State for mobile menu
  const [hidden, setHidden] = useState(false);
  // Drives the shrink/float scroll effect below — desktop-only by design
  // (see the width/marginTop comments), so mobile stays full-width/flush.
  const [isDesktop, setIsDesktop] = useState(false);
  const lastHiddenStateRef = React.useRef(false);

  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const navWrapperRef = React.useRef<HTMLDivElement>(null);

  const handleMouseEnter = (menu: typeof hoveredMenu) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setHoveredMenu(menu);
    if (menu === "healthcare" || menu === "services" || menu === "company") {
      setLastMegaMenu(menu);
    }
  };

  const handleMouseLeave = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredMenu(false);
    }, 200); // 200ms delay to bridge the gap
  };

  const closeMenuImmediately = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setHoveredMenu(false);
  };

  React.useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Belt-and-suspenders close: clicking anywhere outside the nav (not just
  // hovering away from it) closes an open dropdown immediately, regardless
  // of the hover-close timer's 200ms delay.
  React.useEffect(() => {
    if (!hoveredMenu) return;
    const handlePointerDownOutside = (event: MouseEvent) => {
      if (
        navWrapperRef.current &&
        !navWrapperRef.current.contains(event.target as Node)
      ) {
        closeMenuImmediately();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenuImmediately();
    };
    document.addEventListener("mousedown", handlePointerDownOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDownOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [hoveredMenu]);

  // Track the lg breakpoint (same 1024px threshold used by the desktop nav
  // links/dropdowns elsewhere in this file) so the shrink/float scroll
  // effect below can actually stay off on mobile, matching its own comments.
  React.useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const mounted = React.useSyncExternalStore(noopSubscribe, () => true, () => false);

  // Get the actual theme being displayed (resolve "system" to "dark" or "light")
  const getDisplayTheme = () => {
    if (!mounted) return "light"; // Default to light during SSR matching light theme
    if (theme === "system") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return theme;
  };

  const displayTheme = getDisplayTheme();

  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = scrollY.getPrevious() ?? 0;
    const shouldBeHidden = latest > previous && latest > 150;

    // Only update state if the hidden state has actually changed
    if (shouldBeHidden !== lastHiddenStateRef.current) {
      setHidden(shouldBeHidden);
      lastHiddenStateRef.current = shouldBeHidden;
      if (shouldBeHidden) {
        closeMenuImmediately();
      }
    }
  });

  // --- 2. Animate Width ---
  // Mobile: 100% for full width; Desktop: 95% for floating effect
  const width = useTransform(
    scrollY,
    NAVBAR_SCROLL_RANGE,
    isDesktop ? ["100%", "95%"] : ["100%", "100%"],
  );

  // --- 3. Animate "Float" (Margin Top) ---
  // Mobile: no margin, Desktop: 0.5rem → 1rem for floating effect
  const marginTop = useTransform(
    scrollY,
    NAVBAR_SCROLL_RANGE,
    isDesktop ? ["0px", "1rem"] : ["0px", "0px"],
  );

  // --- 4. Animate Rounded Corners ---
  const borderRadius = useTransform(scrollY, NAVBAR_SCROLL_RANGE, [
    "9999px",
    "9999px",
  ]);

  // --- 8. Animate Dropdown Top Position ---
  const dropdownTop = useTransform(scrollY, NAVBAR_SCROLL_RANGE, [
    "5.5rem",
    "5.5rem",
  ]);

  // Check if current hovered menu has dropdown content
  const openMegaMenu: MegaMenuKey | null =
    hoveredMenu === "healthcare" || hoveredMenu === "services" || hoveredMenu === "company"
      ? hoveredMenu
      : null;
  // Keeps the last panel showing while the closed state fades out.
  const shownMegaMenu: MegaMenuKey = openMegaMenu ?? lastMegaMenu;

  const renderMenuTrigger = (menu: MegaMenuKey, label: string) => {
    const isOpen = hoveredMenu === menu;
    return (
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls="nav-mega-menu"
        onMouseEnter={() => handleMouseEnter(menu)}
        onClick={() => (isOpen ? closeMenuImmediately() : handleMouseEnter(menu))}
        className="relative flex cursor-pointer items-center gap-1 rounded-sm py-2 text-foreground/85 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
      >
        {label}
        <ChevronDown
          aria-hidden="true"
          className={`h-3.5 w-3.5 transition-transform motion-reduce:transition-none ${isOpen ? "rotate-180" : ""}`}
        />
        {isOpen && <ActiveLinkIndicator />}
      </button>
    );
  };

  // Hide Navbar on admin pages (placed after all hooks)
  if (pathname?.startsWith("/admin")) {
    return null;
  }

  return (
    <>
      <motion.div
        ref={navWrapperRef}
        variants={{
          visible: { y: 0 },
          hidden: { y: "-100%" },
        }}
        animate={hidden ? "hidden" : "visible"}
        transition={{ duration: 0.35, ease: "easeInOut" }}
        className="fixed px-4 inset-x-0 top-0 z-[100] flex justify-center mt-3"
        onMouseEnter={() => {
          if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
          }
        }}
        onMouseLeave={handleMouseLeave}
      >
        <motion.nav
          style={{
            width,
            marginTop,
            borderRadius,
          }}
          className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 w-full lg:max-w-7xl border border-black/[0.08] dark:border-white/10 bg-white/85 dark:bg-zinc-950/85 text-foreground backdrop-blur-xl shadow-lg shadow-black/[0.04] dark:shadow-black/40"
        >
          {/* --- 1. Logo Container --- */}
          <div
            className="relative flex items-center justify-start w-24 sm:w-28 md:w-36 lg:w-40 h-6 sm:h-7 md:h-8 lg:h-10 cursor-pointer"
            onMouseEnter={() => closeMenuImmediately()}
          >
            <Link href="/" className="flex items-center justify-start w-full h-full">
              <Image
                src="/Logo2-448.webp"
                alt="Woltrio Logo"
                width={224}
                height={42}
                // Not priority: two theme variants exist and one is always
                // hidden, so high-priority preloads only competed with the LCP.
                loading="eager"
                sizes="(max-width: 640px) 96px, (max-width: 768px) 112px, (max-width: 1024px) 144px, 160px"
                className="h-6 sm:h-7 md:h-8 lg:h-10 w-auto object-contain dark:hidden"
              />
              <Image
                src="/Logo4-448.webp"
                alt="Woltrio Logo"
                width={224}
                height={42}
                loading="eager"
                sizes="(max-width: 640px) 96px, (max-width: 768px) 112px, (max-width: 1024px) 144px, 160px"
                className="h-6 sm:h-7 md:h-8 lg:h-10 w-auto object-contain hidden dark:block"
              />
            </Link>
          </div>

          {/* --- 2. Desktop Links Container --- */}
          <div className="hidden lg:flex items-center gap-4 text-sm font-semibold text-foreground">
            {/* --- Healthcare (lead menu) --- */}
            {renderMenuTrigger("healthcare", t.nav.healthcare)}
            <span className="text-foreground/20 select-none">/</span>

            {/* --- Services --- */}
            {renderMenuTrigger("services", t.nav.services)}
            <span className="text-foreground/20 select-none">/</span>

            {/* --- Work --- */}
            <Link
              href="/case-studies"
              onMouseEnter={() => handleMouseEnter("work")}
              className="cursor-pointer relative py-2 text-foreground/85 hover:text-foreground transition-colors"
            >
              {t.nav.work}
              {hoveredMenu === "work" && <ActiveLinkIndicator />}
            </Link>
            <span className="text-foreground/20 select-none">/</span>

            {/* --- Insights --- */}
            <Link
              href="/insights"
              onMouseEnter={() => handleMouseEnter("insights")}
              className="cursor-pointer relative py-2 text-foreground/85 hover:text-foreground transition-colors"
            >
              {t.nav.insights}
              {hoveredMenu === "insights" && <ActiveLinkIndicator />}
            </Link>
            <span className="text-foreground/20 select-none">/</span>

            {/* --- Company (about, team, trust, careers, contact) --- */}
            {renderMenuTrigger("company", t.nav.company)}
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            {/* --- Language (Desktop; mobile has it inside the menu) --- */}
            <div className="hidden md:flex" onMouseEnter={() => closeMenuImmediately()}>
              <LanguageSwitcher variant="compact" label={t.footer.languageLabel} />
            </div>

            {/* --- Theme Toggle (Desktop) --- */}
            <button
              onClick={(e) =>
                triggerThemeTransition(e, () =>
                  setTheme(displayTheme === "dark" ? "light" : "dark"),
                )
              }
              className="hidden md:flex p-3 cursor-pointer rounded-full text-foreground hover:bg-foreground/5 dark:hover:bg-white/10 transition-colors"
              aria-label={
                displayTheme === "dark"
                  ? "Switch to light mode"
                  : "Switch to dark mode"
              }
            >
              <div className="w-5 h-5 flex items-center justify-center">
                {!mounted ? (
                  <span className="w-5 h-5 block" />
                ) : displayTheme === "dark" ? (
                  <Sun size={20} />
                ) : (
                  <Moon size={20} />
                )}
              </div>
            </button>

            {/* --- 3. Button (Desktop) --- */}
            <Button
              href={"/inquiry"}
              variant="primary"
              size="sm"
              className="hidden lg:inline-flex"
              aria-label="Start an inquiry for your project"
            >
              {t.nav.inquiryNow}
            </Button>

            {/* --- Mobile Theme Toggle (Visible on Mobile) --- */}
            <button
              onClick={(e) =>
                triggerThemeTransition(e, () =>
                  setTheme(displayTheme === "dark" ? "light" : "dark"),
                )
              }
              className="md:hidden p-3 -m-1 rounded-full text-foreground hover:bg-foreground/5 dark:hover:bg-white/10 transition-colors"
              aria-label={
                displayTheme === "dark"
                  ? "Switch to light mode"
                  : "Switch to dark mode"
              }
            >
              <div className="w-5 h-5 flex items-center justify-center">
                {!mounted ? (
                  <span className="w-5 h-5 block" />
                ) : displayTheme === "dark" ? (
                  <Sun size={20} />
                ) : (
                  <Moon size={20} />
                )}
              </div>
            </button>

            {/* --- 4. Mobile Menu Toggle --- */}
            <button
              className="lg:hidden z-50 ml-2 flex h-11 w-11 items-center justify-center rounded-full text-foreground hover:bg-foreground/5 dark:hover:bg-white/10 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={
                isMobileMenuOpen
                  ? "Close navigation menu"
                  : "Open navigation menu"
              }
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </motion.nav>

        {/* --- 5. Dropdown Container (Desktop) --- */}
        {/* All three panels are always in the server-rendered HTML so crawlers
            can follow every menu link; the closed state is only visual
            (`invisible` + fade), and inactive panels are `hidden`. */}
        <motion.div
          style={{ top: dropdownTop }}
          className="absolute left-0 right-0 w-full z-40 justify-center hidden lg:flex pointer-events-none"
        >
          <div
            id="nav-mega-menu"
            className={`w-[95%] max-w-7xl mx-auto rounded-2xl overflow-hidden border border-border bg-background text-foreground shadow-xl shadow-black/[0.06] dark:shadow-black/40 transition-[opacity,transform,visibility] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none ${
              openMegaMenu
                ? "visible pointer-events-auto translate-y-0 opacity-100"
                : "invisible -translate-y-2 opacity-0"
            }`}
          >
            {MEGA_MENUS.map((menu) => (
              <div key={menu} hidden={menu !== shownMegaMenu}>
                <MegaMenuPanel
                  menu={menu}
                  locale={locale}
                  onNavigate={closeMenuImmediately}
                  socials={socials}
                />
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>
      {/* --- End of main wrapper div --- */}

      {/* --- 6. Mobile Menu (Full Screen) --- */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu onClose={() => setIsMobileMenuOpen(false)} socials={socials} />
        )}
      </AnimatePresence>
    </>
  );
}

export default Navbar;